if (!customElements.get("product-modal")) {
  customElements.define(
    "product-modal",
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.zoom = null;
      }

      hide() {
        this.teardownZoom();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
        this.setupZoom();
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute("data-media-id")}"])`,
        ).forEach((element) => {
          element.classList.remove("active");
        });
        const activeMedia = this.querySelector(
          `[data-media-id="${this.openedBy.getAttribute("data-media-id")}"]`,
        );
        const activeMediaTemplate = activeMedia.querySelector("template");
        const activeMediaContent = activeMediaTemplate
          ? activeMediaTemplate.content
          : null;
        activeMedia.classList.add("active");
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == "DEFERRED-MEDIA" &&
          activeMediaContent &&
          activeMediaContent.querySelector(".js-youtube")
        )
          activeMedia.loadContent();
      }

      /* ------- Mobile pinch-to-zoom & drag ------- */
      setupZoom() {
        // Only on small (touch) screens; desktop keeps the scroll-through behavior.
        if (!window.matchMedia("(max-width: 749px)").matches) return;

        const active = this.querySelector(
          ".product-media-modal__content .active",
        );
        if (!active || active.tagName !== "IMG") return;

        const img = active;
        const container = this.querySelector(".product-media-modal__content");
        const state = { scale: 1, x: 0, y: 0 };
        const pointers = new Map();
        let start = null;
        let lastTap = 0;

        const apply = () => {
          img.style.transform = `translate(${state.x}px, ${state.y}px) scale(${state.scale})`;
        };

        // Keep the image from being dragged fully outside the viewport.
        const clamp = () => {
          const rect = container.getBoundingClientRect();
          const maxX = Math.max(
            0,
            (img.offsetWidth * state.scale - rect.width) / 2,
          );
          const maxY = Math.max(
            0,
            (img.offsetHeight * state.scale - rect.height) / 2,
          );
          state.x = Math.min(maxX, Math.max(-maxX, state.x));
          state.y = Math.min(maxY, Math.max(-maxY, state.y));
        };

        const reset = () => {
          state.scale = 1;
          state.x = 0;
          state.y = 0;
          apply();
        };

        const onDown = (e) => {
          pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
          img.setPointerCapture(e.pointerId);

          if (pointers.size === 1) {
            start = {
              mode: "pan",
              x: e.clientX,
              y: e.clientY,
              sx: state.x,
              sy: state.y,
            };

            // double-tap toggles zoom (skip when a pinch is starting)
            const now = e.timeStamp;
            if (now - lastTap < 300) {
              const rect = container.getBoundingClientRect();
              if (state.scale > 1) {
                reset();
              } else {
                const target = 2.5;
                const dx = e.clientX - (rect.left + rect.width / 2);
                const dy = e.clientY - (rect.top + rect.height / 2);
                state.scale = target;
                state.x = dx * (1 - target);
                state.y = dy * (1 - target);
                clamp();
                apply();
              }
              lastTap = 0;
              start = null;
            } else {
              lastTap = now;
            }
          } else if (pointers.size === 2) {
            const [p1, p2] = [...pointers.values()];
            const rect = container.getBoundingClientRect();
            start = {
              mode: "pinch",
              dist: Math.hypot(p2.x - p1.x, p2.y - p1.y),
              scale: state.scale,
              cx: rect.left + rect.width / 2,
              cy: rect.top + rect.height / 2,
              mx: (p1.x + p2.x) / 2,
              my: (p1.y + p2.y) / 2,
              sx: state.x,
              sy: state.y,
            };
          }
        };

        const onMove = (e) => {
          if (!pointers.has(e.pointerId)) return;
          pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

          if (pointers.size >= 2 && start && start.mode === "pinch") {
            const [p1, p2] = [...pointers.values()];
            const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
            const newScale = Math.min(
              4,
              Math.max(1, start.scale * (dist / start.dist)),
            );
            // keep the pinch mid-point anchored under the fingers
            const dx = start.mx - start.cx;
            const dy = start.my - start.cy;
            const ratio = newScale / start.scale;
            state.x = dx - ratio * (dx - start.sx);
            state.y = dy - ratio * (dy - start.sy);
            state.scale = newScale;
            clamp();
            apply();
            e.preventDefault();
          } else if (
            pointers.size === 1 &&
            start &&
            start.mode === "pan" &&
            state.scale > 1
          ) {
            state.x = start.sx + (e.clientX - start.x);
            state.y = start.sy + (e.clientY - start.y);
            clamp();
            apply();
            e.preventDefault();
          }
        };

        const onUp = (e) => {
          pointers.delete(e.pointerId);
          try {
            img.releasePointerCapture(e.pointerId);
          } catch (err) {
            /* no-op */
          }

          if (pointers.size === 1) {
            const [p] = [...pointers.values()];
            start = { mode: "pan", x: p.x, y: p.y, sx: state.x, sy: state.y };
          } else if (pointers.size === 0) {
            start = null;
            if (state.scale <= 1.01) reset();
          }
        };

        img.style.willChange = "transform";
        img.addEventListener("pointerdown", onDown);
        img.addEventListener("pointermove", onMove);
        img.addEventListener("pointerup", onUp);
        img.addEventListener("pointercancel", onUp);

        this.zoom = { img, handlers: { onDown, onMove, onUp } };
      }

      teardownZoom() {
        if (!this.zoom) return;
        const { img, handlers } = this.zoom;
        img.removeEventListener("pointerdown", handlers.onDown);
        img.removeEventListener("pointermove", handlers.onMove);
        img.removeEventListener("pointerup", handlers.onUp);
        img.removeEventListener("pointercancel", handlers.onUp);
        img.style.transform = "";
        img.style.willChange = "";
        this.zoom = null;
      }
    },
  );
}
